import React, { Component } from 'react';
import b_ from 'b_';
import cn from 'classnames';
import PropTypes from 'prop-types';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseButton from '../FuseControls/FuseButton';
import './UrlGuidelines.css';

const block = b_.lock('UrlGuidelines');

export default class UrlGuidelines extends Component {
    static propTypes = {
        header: PropTypes.string,
        onClose: PropTypes.func.isRequired,
        className: PropTypes.string,
        legacy: PropTypes.bool,
    };

    renderLegacy() {
        const { header, onClose, className } = this.props;

        return <div className={cn(block('Wrapper'), className)}>
            <FuseAlert
                type="success"
                icon="info"
                title="Note - "
                subtitle={<>If you need to define URL patterns using IP addresses or CIDR blocks, please&nbsp;
                    <a
                        href="/console/product_settings/new_policies_migration"
                        target="_blank"
                        style={{ color: '#0277BD' }}
                    >migrate to the new policy</a>.
                </>}
                style={{ marginBottom: 10 }}
            />
            <div className={block()}>
                <div className={block('Header')}>
                    {header || 'Syntax Requirements for adding URLs:'}
                    <FuseButton icon="close" primary className={block('Close')} onClick={onClose} />
                </div>
                <div className={block('Content')}>
                    <p>
                        URLs are defined by match patterns that start with a permitted scheme.
                        Each match pattern has 3 parts:
                    </p>
                    <p>
                        <b>1. Scheme</b> - for example, http or https<br />
                        <b>2. Host</b> - for example, as a domain (www.google.com, or *.google.com)<br />
                        <b>3. Path and query</b> - for example, /*, /foo*, or /foo/bar
                    </p>
                    <p>
                        The meaning of ‘*’ depends on whether it’s in the scheme, host, or path. If the scheme is *,
                        then it matches either http or https. If the host is just *, then it matches any host.
                        In the path section, each ‘*’ matches 0 or more characters.
                    </p>
                </div>
            </div>
        </div>;
    }

    render() {
        const { header, onClose, className, legacy } = this.props;

        if (legacy) {
            return this.renderLegacy();
        }

        return <div className={cn(block(), className)}>
            <div className={block('Header')}>
                {header || 'Syntax Requirements for adding IP addresses and URLs:'}
                <FuseButton icon="close" primary className={block('Close')} onClick={onClose} />
            </div>
            <div className={block('Content')}>
                <p>
                    IP addresses and URLs are defined by match patterns that start with a permitted scheme.
                    Each match pattern has 3 parts:
                </p>
                <p>
                    <b>1. Scheme</b> - for example, http or https<br />
                    <b>2. Host</b> - for example, as a domain (www.google.com, or *.google.com) or an IP address
                    with a CIDR notation (*://93.184.216.34/24)<br />
                    <b>3. Path and query</b> - for example, /*, /foo*, or /foo/bar
                </p>
                <p>
                    The meaning of ‘*’ depends on whether it’s in the scheme, host, or path. If the scheme is *, then it
                    matches either http or https. If the host is just *, then it matches any host. In the path section,
                    each ‘*’ matches 0 or more characters.
                </p>
                <p>
                    If a number is the first element that follows a domain represented as an IP address, the Isolation
                    system will treat that part of the URL expression as a CIDR, and not as a path. So
                    *://93.184.216.34/<b>24</b> will be treated as a CIDR block.
                </p>
                <p>
                    If you would like to specify a number as a
                    path in the URL expression, to prevent it from being interpreted as a CIDR, you can either use a
                    wildcard in the number, or use a domain name instead of an IP address. So
                    *://93.184.216.34/<b>2*</b>, or *://<b>www.example.com</b>/24 will be interpreted as paths by the
                    Isolation system.
                </p>
                <table className={block('Table')}>
                    <thead><tr>
                        <th>Pattern and use case</th>
                        <th>example URLs it will match</th>
                        <th>example URLs it will NOT match</th>
                    </tr></thead>
                    <tbody><tr>
                        <td>
                            *://example.com/*<br/>
                            match exact domain
                        </td>
                        <td>
                            http://example.com<br/>
                            https://example.com/path?query<br/>
                        </td>
                        <td>
                            https://sub.example.com<br />
                            https://notexample.com<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://*.example.com/*<br/>
                            match domain and all subdomains
                        </td>
                        <td>
                            http://example.com<br/>
                            http://www.example.com<br/>
                            https://sub.domain.example.com/path?query<br/>
                        </td>
                        <td>
                            https://example.org<br />
                            https://notexample.org<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://example.com/page.html<br/>
                            match exact domain, path and query
                        </td>
                        <td>
                            http://example.com/page.html<br/>
                            https://example.com/page.html<br/>
                        </td>
                        <td>
                            http://www.example.com/<br/>
                            http://www.example.com/page.html<br/>
                            https://example.com/page.html?query<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://*.example.com/files*<br/>
                            match domain with all subdomains<br/>
                            and beginning of a path
                        </td>
                        <td>
                            http://example.com/files<br/>
                            https://example.com/files/folder<br/>
                            https://www.example.com/files?download<br/>
                            https://sb.example.com/files/d?u=file.exe<br/>
                        </td>
                        <td>
                            http://www.example.com/<br/>
                            https://example.com/not/files/<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://*/*file.exe*<br/>
                            match part of path or query
                        </td>
                        <td>
                            http://example.com/file.exe<br/>
                            http://example.org/download/file.exe<br/>
                            http://example.net/download?u=file.exe&t=123<br/>
                        </td>
                        <td>
                            http://example.com/file<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://*/*?*file.exe*<br/>
                            match part of a query
                        </td>
                        <td>
                            http://example.org/?url=file.exe<br/>
                            http://example.net/path?file.exe&t=123<br/>
                        </td>
                        <td>
                            http://example.com/file<br/>
                            http://example.com/file.exe<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://93.184.216.34/*<br/>
                            match by IP address
                        </td>
                        <td>
                            http://93.184.216.34<br/>
                            http://example.com<br/>
                            http://example.org<br/>
                        </td>
                        <td>
                            http://google.com<br/>
                            http://93.184.216.134<br/>
                        </td>
                    </tr><tr>
                        <td>
                            *://93.184.216.34/24<br/>
                            match subnet
                        </td>
                        <td>
                            http://93.184.216.34<br/>
                            http://93.184.216.56<br/>
                            http://example.com<br/>
                            http://example.org<br/>
                        </td>
                        <td>
                            http://google.com<br/>
                            http://93.1.1.1<br/>
                        </td>
                    </tr></tbody>
                </table>
            </div>
        </div>;
    }
}
